import { milestoneNotificationFix } from '~/server/common/constants';
import { OLD_DRAFT_LEAD_TEXT, OLD_DRAFT_NOTICE_DAYS } from '~/server/common/draft-reaping';
import { NotificationCategory } from '~/server/common/enums';
import {
  createNotificationProcessor,
  notBlockedBetween,
} from '~/server/notifications/base.notifications';
import { getDisplayName, slugit, withDefiniteArticle } from '~/utils/string-helpers';

const modelDownloadMilestones = [5, 10, 20, 50, 100, 500] as const;
const modelLikeMilestones = [100, 500, 1000, 10000, 50000] as const;

export const modelNotifications = createNotificationProcessor({
  'model-download-milestone': {
    displayName: 'Model download milestones',
    category: NotificationCategory.Milestone,
    prepareMessage: ({ details }) => ({
      message: `Congrats! Your ${
        details.modelName
      } model has received ${details.downloadCount.toLocaleString()} downloads`,
      url: `/models/${details.modelId}`,
    }),
    prepareQuery: async ({ lastSentDate, clickhouse }) => {
      if (!clickhouse) return;
      const affected = await clickhouse.$query<{ modelId: number }>`
        SELECT DISTINCT modelId
        FROM modelVersionEvents
        WHERE time > ${lastSentDate}
        AND type = 'Download'
      `;

      const affectedJson = JSON.stringify(affected.map((x) => x.modelId));
      return `
        WITH milestones AS (
          SELECT * FROM (VALUES ${modelDownloadMilestones.map((x) => `(${x})`).join(', ')}) m(value)
        ), model_value AS (
          SELECT
            "modelId" model_id,
            "downloadCount" download_count
          FROM "ModelMetric"
          WHERE
            "modelId" = ANY (SELECT json_array_elements('${affectedJson}'::json)::text::integer)
            AND "downloadCount" >= ${modelDownloadMilestones[0]}
        ), model_milestone AS (
          SELECT
            m."userId" "ownerId",
            JSON_BUILD_OBJECT(
              'modelName', m.name,
              'modelId', m.id,
              'downloadCount', ms.value
            ) "details"
          FROM model_value mval
          JOIN "Model" m on m.id = mval.model_id
          JOIN milestones ms ON ms.value <= mval.download_count
          WHERE m."createdAt" > '${milestoneNotificationFix}'
        )
        SELECT
          CONCAT('model-download-milestone:', details->>'modelId', ':', details->>'downloadCount') as "key",
          "ownerId"    "userId",
          'model-download-milestone' "type",
          details
        FROM model_milestone
        WHERE NOT EXISTS (SELECT 1 FROM "UserNotificationSettings" WHERE "userId" = "ownerId" AND type = 'model-download-milestone')
      `;
    },
  },
  'model-like-milestone': {
    displayName: 'Model like milestones',
    category: NotificationCategory.Milestone,
    prepareMessage: ({ details }) => {
      const count = details.favoriteCount || details.thumbsUpCount;

      return {
        message: `Congrats! Your ${
          details.modelName
        } model has received ${count?.toLocaleString()} likes`,
        url: `/models/${details.modelId}`,
      };
    },
    prepareQuery: ({ lastSent }) => `
      WITH milestones AS (
        SELECT * FROM (VALUES ${modelLikeMilestones.map((x) => `(${x})`).join(', ')}) m(value)
      ), model_value AS (
        SELECT DISTINCT
          mm."modelId" model_id,
          mm."thumbsUpCount" thumbs_up_count
        FROM "ModelMetric" mm
        JOIN "Model" m ON m.id = mm."modelId"
        WHERE
          mm."updatedAt" > '${lastSent}'
          AND "thumbsUpCount" >= ${modelLikeMilestones[0]}
          AND m."userId" > 0
      ), model_milestone AS (
        SELECT
          m."userId" "ownerId",
          JSON_BUILD_OBJECT(
            'modelName', m.name,
            'modelId', m.id,
            'thumbsUpCount', ms.value
          ) "details"
        FROM model_value mval
        JOIN "Model" m on m.id = mval.model_id
        JOIN milestones ms ON ms.value <= mval.thumbs_up_count
        WHERE m."createdAt" > '${milestoneNotificationFix}'
      )
      SELECT
        CONCAT('model-like-milestone:', details->>'modelId', ':', details->>'thumbsUpCount') "key",
        "ownerId"    "userId",
        'model-like-milestone' "type",
        details
      FROM model_milestone
      WHERE NOT EXISTS (SELECT 1 FROM "UserNotificationSettings" WHERE "userId" = "ownerId" AND type = 'model-like-milestone')
    `,
  },
  // Moveable
  'new-model-version': {
    displayName: 'New versions of models you follow',
    category: NotificationCategory.Update,
    prepareMessage: ({ details }) => ({
      message: `${withDefiniteArticle(details.modelName, 'The')} model has a new version: ${
        details.versionName
      }`,
      url: `/models/${details.modelId}${
        details.modelVersionId ? `?modelVersionId=${details.modelVersionId}` : ''
      }`,
    }),
    prepareQuery: ({ lastSent }) => `
      WITH new_model_version AS (
        SELECT
          m."userId",
          mv."modelId",
          JSONB_BUILD_OBJECT(
            'modelId', mv."modelId",
            'modelName', m.name,
            'versionName', mv.name,
            'modelVersionId', mv.id
          ) "details"
        FROM "ModelVersion" mv
        JOIN "Model" m ON m.id = mv."modelId"
        WHERE m."userId" > 0
          AND mv."publishedAt" - m."publishedAt" > INTERVAL '2 hour'
          -- Wall-clock floor: bound the query window to the last 30 minutes regardless of
          -- how far behind the cursor is. Prevents death-spiral when a stale cursor would
          -- otherwise force a giant table scan; older events become unreachable rather
          -- than blocking every subsequent run.
          AND mv."publishedAt" > NOW() - INTERVAL '30 minutes'
          AND (
            -- handle scheduled posts - these can take a little while to update via another job
            (mv."publishedAt" BETWEEN '${lastSent}'::timestamptz - interval '59 second' AND now() AND mv.status = 'Published')
            OR (mv."publishedAt" <= '${lastSent}' AND mv.status = 'Scheduled')
          )
      ), followers AS (
        SELECT DISTINCT ON ("userId")
          *
        FROM (
          SELECT
            ue."userId",
            nmv.details
          FROM "UserEngagement" ue
          JOIN new_model_version nmv ON nmv."userId" = ue."targetUserId"
          WHERE ue.type = 'Follow'
            AND NOT EXISTS (SELECT 1 FROM "ModelEngagement" me WHERE me.type = 'Mute' AND me."userId" = ue."userId" AND me."modelId" = nmv."modelId")
            AND ${notBlockedBetween('ue."userId"', 'nmv."userId"')}

          UNION

          SELECT
            me."userId",
            nmv.details
          FROM "ModelEngagement" me
          JOIN new_model_version nmv ON nmv."modelId" = me."modelId"
          WHERE type = 'Notify'
            AND ${notBlockedBetween('me."userId"', 'nmv."userId"')}
        ) t
      )
      SELECT
        CONCAT('new-model-version:', details->>'modelVersionId') "key",
        "userId",
        'new-model-version' "type",
        details
      FROM followers n
      WHERE NOT EXISTS (SELECT 1 FROM "UserNotificationSettings" uns WHERE uns."userId" = n."userId" AND type = 'new-model-version')
    `,
  },
  // Moveable
  'new-model-from-following': {
    displayName: 'New models from followed users',
    category: NotificationCategory.Update,
    prepareMessage: ({ details }) => ({
      message: `${details.username} released a new ${getDisplayName(details.modelType)}: ${
        details.modelName
      }`,
      url: `/models/${details.modelId}`,
    }),
    prepareQuery: ({ lastSent }) => `
      WITH new_model_from_following AS (
        SELECT DISTINCT
          ue."userId" "ownerId",
          JSONB_BUILD_OBJECT(
            'modelId', m."id",
            'modelName', m.name,
            'username', u.username,
            'modelType', m.type
          ) "details"
        FROM "Model" m
        JOIN "User" u ON u.id = m."userId"
        JOIN "UserEngagement" ue ON ue."targetUserId" = m."userId" AND m."publishedAt" >= ue."createdAt" AND ue.type = 'Follow'
        WHERE
          m."userId" != -1
          -- 59s lookback buffer: handles race condition between send-notifications and
          -- process-scheduled-publishing jobs (both */1 * * * *). Without it, a model can be
          -- missed if its publishedAt falls just before the cursor after the previous run advanced.
          -- Duplicates are safe due to ON CONFLICT upsert in PendingNotification.
          AND m."publishedAt" BETWEEN '${lastSent}'::timestamptz - interval '59 second' AND now()
          -- Wall-clock floor: bound the query window to the last 30 minutes regardless of
          -- how far behind the cursor is. Prevents death-spiral when a stale cursor would
          -- otherwise force a giant Model x UserEngagement scan.
          AND m."publishedAt" > NOW() - INTERVAL '30 minutes'
          AND m.status IN ('Published', 'Scheduled')
          -- The follow almost always predates the block, so the Follow row outlives it and keeps
          -- delivering the blocker's releases to someone who blocked them.
          AND ${notBlockedBetween('ue."userId"', 'm."userId"')}
      )
      SELECT
        CONCAT('new-model-from-following:', details->>'modelId') "key",
        "ownerId"    "userId",
        'new-model-from-following' "type",
        details
      FROM new_model_from_following
      WHERE NOT EXISTS (SELECT 1 FROM "UserNotificationSettings" WHERE "userId" = "ownerId" AND type = 'new-model-from-following')
    `,
  },
  'early-access-complete': {
    toggleable: false,
    displayName: 'Early Access Complete',
    category: NotificationCategory.Update,
    prepareMessage: ({ details }) => ({
      message: `${details.modelName}: ${details.versionName} has left Early Access!`,
      url: `/models/${details.modelId}?modelVersionId=${details.versionId}`,
    }),
    prepareQuery: ({ lastSent }) => `
      WITH early_access_versions AS (
         SELECT
          mv.id version_id,
          mv.name version_name,
          m.id model_id,
          m.name model_name,
          m.type model_type,
          mv."publishedAt" updated_published_at
        FROM "ModelVersion" mv
        JOIN "Model" m ON m.id = mv."modelId"
        JOIN "PaidAccess" pa ON pa."entityType" = 'ModelVersion' AND pa."entityId" = mv.id
        where
          pa."endsAt" IS NOT NULL
          AND pa."endsAt" <= NOW()
          AND pa."endsAt" >= '${lastSent}'
        -- Wall-clock floor: bound to last 30 minutes so a stale cursor cannot force a
        -- giant table scan. See new-model-from-following / new-model-version for details.
        AND pa."endsAt" > NOW() - INTERVAL '30 minutes'
      ), early_access_complete AS (
        SELECT DISTINCT
          mve."userId" owner_id,
          jsonb_build_object(
            'modelId', model_id,
            'modelName', model_name,
            'modelType', model_type,
            'versionId', version_id,
            'versionName', version_name
          ) details
        FROM early_access_versions ev
        JOIN "ModelVersionEngagement" mve ON mve."modelVersionId" = ev.version_id AND mve.type = 'Notify'
        WHERE ev.updated_published_at > '${lastSent}' AND ev.updated_published_at < now()
      )
      SELECT
        concat('early-access-complete:', details->>'versionId') "key",
        owner_id "userId",
        'early-access-complete' "type",
        details
      FROM early_access_complete;
    `,
  },
  'old-draft': {
    displayName: 'Old Model Draft Deletion Reminder',
    category: NotificationCategory.System,
    toggleable: false,
    prepareMessage: ({ details }) => ({
      message: `Your ${details.modelName} model that is in draft mode will be deleted in ${OLD_DRAFT_LEAD_TEXT}.`,
      url: `/models/${details.modelId}/${slugit(details.modelName)}`,
    }),
    /**
     * Selects models the reaper in `src/server/jobs/remove-old-drafts.ts` is
     * heading for, `OLD_DRAFT_LEAD_DAYS` ahead of time. The message promises the
     * deletion is coming and is `toggleable: false`, so a model this EXCLUDES is
     * a model destroyed with no notice at all.
     *
     * 🔴 THE INVARIANT, with its two exclusions named — it is NOT an absolute,
     * and an earlier revision of this comment wrongly stated it as one:
     *
     *     For a model in `Draft`, no term here may exclude a model the reaper
     *     will destroy, EXCEPT via the two carve-outs below.
     *
     *   1. `status`. The reaper destroys `('Draft', 'Deleted')`; this warns on
     *      `Draft` only, so a `Deleted` model is cascade-deleted unwarned. That
     *      is deliberate — a user who deleted a model has already expressed the
     *      intent — and it is pinned by a test so it stays visible. Widening this
     *      to `Deleted` is a product decision, not a bug fix.
     *   2. `downloadCount`, which is an ASSUMPTION rather than a property. See
     *      the value-term note below for the path on which it fails.
     *
     * Outside those two, errors are one-directional — warn, then spare.
     *
     * 🔴 THAT IS WHY THERE IS NO ACTIVITY FENCE HERE, and re-adding one is a bug.
     * The reaper carries two `NOT EXISTS` clauses over recent ModelVersion /
     * ModelFile activity. They cannot be mirrored into this query, and the reason
     * is a difference in EVALUATION SCHEDULE, not in wording:
     *
     *   - the reaper is a nightly cron that RETRIES FOREVER, so it fires at
     *     `max(U + REAP_AGE_DAYS, activity + ACTIVITY_WINDOW_DAYS)` — note the
     *     two constants are distinct quantities that happen to both be 30 today,
     *     so do NOT collapse this to one of them (see `draft-reaping.ts`);
     *   - this notification evaluates ONCE, in a ~1-minute band at
     *     `U + OLD_DRAFT_NOTICE_DAYS`, and is never re-evaluated for that `U`.
     *
     * So any `now()`-relative activity clause here is asking the question at the
     * wrong instant. Worked example: model row last written Jan 1, version and
     * file land Jan 3 — which `remove-old-drafts.ts` documents as the NORM, "the
     * finished resource lands hours or weeks later". The notification runs Jan 24
     * with a cutoff of Jan 1, sees activity on Jan 3, excludes the model, and
     * never looks again. The reaper first fires Feb 3 with a cutoff of Jan 4, the
     * fence clears, and the model plus its versions, files and training data are
     * cascade-deleted — unwarned. Two successive revisions of this PR shipped a
     * variant of that bug; the fences are gone rather than re-tuned because no
     * choice of interval fixes a predicate evaluated at a single instant against
     * a condition that keeps moving.
     *
     * Dropping them costs nothing this notification was for: every one of the
     * ~1,308 false alarms this predicate was tightened to prevent came from
     * `downloadCount >= 10`, not from activity.
     *
     * WHY THE VALUE TERMS MAY BE MIRRORED, when the fences may not — they do not
     * depend on predicting WHEN the reaper fires:
     *
     *   - `availability` is SAFE. It is NOT NULL with a `Public` default, and its
     *     only raw-SQL writer (`entityAvailabilityUpdate` in
     *     `src/server/services/common.service.ts`) sets `"updatedAt" = NOW()` in
     *     the same statement, so a change bumps `Model."updatedAt()"` and re-arms
     *     this band at the new `U` as well as resetting the reaper's clock.
     *
     *   - 🔴 `downloadCount` is an ASSUMPTION, not a property — do not restate it
     *     as one. It has NO incrementing writer: every write is a full recompute
     *     (`src/server/metrics/model.metrics.ts`, a `SUM` over surviving
     *     `ModelVersionMetric` rows), so it is DECREASING-CAPABLE by
     *     construction. The failure path, concretely: a Draft model rolled up to
     *     12 is excluded here at day 23. A version carrying 8 downloads is then
     *     deleted; `deleteVersionById` calls `updateModelLastVersionAt`, which
     *     early-returns when the model has no `Published` version — true for a
     *     Draft — so `Model."updatedAt"` is NOT bumped and this band never
     *     re-arms. A later recompute yields 4, the reaper's `< 10` becomes true,
     *     and the model is cascade-deleted UNWARNED.
     *
     *     That is architectural, not a bug to patch here: the band gives exactly
     *     one evaluation per `U` (its lower edge is `lastSent - notice`), so
     *     there is no second chance to catch the change. It is recorded so the
     *     next person does not read `downloadCount` as a safe term.
     *
     * ⚠ Residual directions OUTSIDE the two exclusions above, both harmless: in
     * the gap before the reap, `downloadCount` can rise past 10 or new activity
     * can arrive, either of which makes the reaper SPARE a model this warned; and
     * because the reaper retries, the real gap can exceed `OLD_DRAFT_LEAD_DAYS`,
     * so the warning may arrive earlier than the message implies.
     */
    prepareQuery: ({ lastSent }) => `
      with to_add AS (
        SELECT DISTINCT
          m."userId",
          jsonb_build_object(
            'modelId', m.id,
            'modelName', m.name,
            'updatedAt', m."updatedAt"
          ) details
        FROM "Model" m
        WHERE m.status IN ('Draft')
        AND m."updatedAt" BETWEEN '${lastSent}'::timestamp - INTERVAL '${OLD_DRAFT_NOTICE_DAYS} days' AND NOW() - INTERVAL '${OLD_DRAFT_NOTICE_DAYS} days'
        AND m."availability" != 'Private'::"Availability"
        -- EXISTS, not a JOIN, but the same semantics as the reaper's INNER
        -- JOIN + DISTINCT: a model with no "ModelMetric" row is not reapable,
        -- so it must not be warned either.
        AND EXISTS (SELECT 1 FROM "ModelMetric" mm
                     WHERE mm."modelId" = m.id
                       AND mm."downloadCount" < 10)
      )
      SELECT
        concat('old-draft:', details->>'modelId', ':', details->>'updatedAt') "key",
        "userId",
        'old-draft' "type",
        details
      FROM to_add
    `,
  },

  'early-access-failed-to-publish': {
    displayName: 'Model version failed to publish.',
    category: NotificationCategory.System,
    toggleable: false,
    prepareMessage: ({ details }) => ({
      message: `We were unable to publish your model version: ${details.displayName} due to insufficient funds. Please remove early access or purchase more Buzz to publish.`,
      url: `/models/${details.modelId}?modelVersionId=${details.modelVersionId}`,
    }),
  },
  'model-hash-fix': {
    displayName: 'Model Hash Fix',
    category: NotificationCategory.System,
    toggleable: false,
    prepareMessage: ({ details }) => ({
      message: `The hash in the metadata of your resource "${details.modelName}: ${details.versionName}" has been corrected. Please redownload it to ensure that your images report the correct hash going forward.`,
      url: `/models/${details.modelId}/${slugit(details.modelName)}?modelVersionId=${
        details.versionId
      }`,
    }),
  },
});
